import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import ThesisDetail from './pages/ThesisDetail';
import ParticleBackground from './components/ParticleBackground';

const PAGE_TITLES = {
  '/': 'About',
  '/projects': 'Projects',
  '/reads': 'Reads',
  '/listens': 'Podcast Notes'
};

const getPageTitle = (pathname) => {
  const normalizedPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  if (PAGE_TITLES[normalizedPath]) return PAGE_TITLES[normalizedPath];
  if (normalizedPath.startsWith('/podcasts/')) return 'Podcast Notes';
  if (normalizedPath.startsWith('/listens/')) return 'Podcast Notes';

  if (normalizedPath.startsWith('/theses/')) {
    const slug = normalizedPath.split('/').filter(Boolean).pop();
    const readableTitle = slug
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return `${readableTitle} Thesis`;
  }

  return 'Page Not Found';
};

function RouteAccessibility() {
  const { pathname } = useLocation();
  const pageTitle = getPageTitle(pathname);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    document.title = `${pageTitle} | Alex Bock`;

    // Clear the live region before reusing an announcement. This ensures that
    // navigation between routes with the same generic title (such as two
    // podcast notes) is still announced by assistive technology.
    setAnnouncement('');
    const announcementTimer = window.setTimeout(() => {
      const isPodcastDetail = pathname.startsWith('/podcasts/') || pathname.startsWith('/listens/');
      setAnnouncement(
        isPodcastDetail
          ? 'Podcast notes detail page loaded'
          : `${pageTitle} page loaded`
      );
    }, 50);

    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.scrollTop = 0;
      mainContent.focus({ preventScroll: true });
    }
    window.scrollTo(0, 0);

    return () => window.clearTimeout(announcementTimer);
  }, [pageTitle, pathname]);

  return (
    <div className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
      {announcement}
    </div>
  );
}

function StandalonePage({ children }) {
  return (
    <main id="main-content" className="standalone-main" tabIndex="-1">
      {children}
    </main>
  );
}

function App() {
  const [particlesEnabled, setParticlesEnabled] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }

    const connection = typeof navigator === 'undefined'
      ? null
      : navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    return (
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      && !connection?.saveData
    );
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;

    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const syncAutomaticParticlePreference = () => {
      setParticlesEnabled(!reducedMotionQuery.matches && !connection?.saveData);
    };

    if (reducedMotionQuery.addEventListener) {
      reducedMotionQuery.addEventListener('change', syncAutomaticParticlePreference);
    } else {
      reducedMotionQuery.addListener(syncAutomaticParticlePreference);
    }
    connection?.addEventListener?.('change', syncAutomaticParticlePreference);

    return () => {
      if (reducedMotionQuery.removeEventListener) {
        reducedMotionQuery.removeEventListener('change', syncAutomaticParticlePreference);
      } else {
        reducedMotionQuery.removeListener(syncAutomaticParticlePreference);
      }
      connection?.removeEventListener?.('change', syncAutomaticParticlePreference);
    };
  }, []);

  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <ParticleBackground isEnabled={particlesEnabled} />
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <RouteAccessibility />
        <Routes>
          <Route
            path="/theses/:id"
            element={<StandalonePage><ThesisDetail /></StandalonePage>}
          />
          <Route path="/*" element={<Layout />} />
        </Routes>
      </Router>
    </div>
  );
}

export default App;
